import { useState, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { AVATAR_WEBP } from "@/lib/optimizedImages";

/**
 * Service pages surfaced via the "Services" dropdown.
 * These are real, existing route destinations — no fabricated links.
 */
const SERVICE_ITEMS = [
  {
    name: "GoHighLevel RevOps Architect",
    href: "/gohighlevel-revops-architect",
  },
  { name: "HubSpot RevOps Architect", href: "/hubspot-revops-architect" },
  {
    name: "Zapier & Make.com Automation",
    href: "/zapier-make-automation-expert",
  },
  {
    name: "Close.com Sales Architecture",
    href: "/close-crm-automation-specialist",
  },
  { name: "n8n Workflow Developer", href: "/n8n-workflow-developer" },
  { name: "Voice AI & Vapi Automation", href: "/voice-ai-vapi-automation" },
  {
    name: "Meta Ads Performance Specialist",
    href: "/meta-ads-performance-specialist",
  },
  {
    name: "Google Ads Specialist",
    href: "/google-ads-performance-specialist",
  },
  { name: "SEO & GEO Architect", href: "/seo-geo-architect" },
];

/**
 * Resources surfaced via the "Resources" dropdown (right-most).
 * Blog + FAQ are grouped here to keep the top-level capsule clean.
 */
const RESOURCE_ITEMS = [
  { name: "Blog", href: "/blog" },
  { name: "FAQ", href: "/#faq", id: "faq" },
];

const AVATAR_URL = AVATAR_WEBP;

/** Plain in-page anchor links rendered directly in the capsule. */
const NAV_ITEMS = [
  { name: "Work", href: "/#moat", id: "work" },
  {
    name: "Architecture",
    href: "/#architecture-showcase",
    id: "architecture-showcase",
  },
  { name: "Story", href: "/#chapter-story", id: "chapter-story" },
];

/** All section IDs tracked for active-section detection (incl. FAQ inside Resources). */
const SECTION_IDS = [
  { name: "Work", id: "work" },
  { name: "Architecture", id: "architecture-showcase" },
  { name: "Story", id: "chapter-story" },
  { name: "FAQ", id: "faq" },
];

type DropItem = { name: string; href: string; id?: string };

/**
 * Reusable desktop dropdown used for both "Services" and "Resources".
 */
const NavDropdown = ({
  label,
  eyebrow,
  items,
  isOpen,
  setIsOpen,
  isScrolled,
  activeId,
  onNavigate,
}: {
  label: string;
  eyebrow?: string;
  items: DropItem[];
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  isScrolled: boolean;
  activeId?: string;
  onNavigate: () => void;
}) => {
  const closeTimer = useRef<number | null>(null);
  const isActive = items.some((i) => i.id && i.id === activeId);

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        setIsOpen(true);
      }}
      onMouseLeave={() => {
        closeTimer.current = window.setTimeout(() => setIsOpen(false), 140);
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className={`relative flex items-center gap-1.5 font-sans font-semibold rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A7FF] ${
          isOpen || isActive
            ? "text-white bg-white/10"
            : "text-white/75 hover:text-white hover:bg-white/[0.07]"
        }`}
        style={{
          fontSize: isScrolled ? "14px" : "15px",
          padding: isScrolled ? "0 16px" : "0 20px",
          height: isScrolled ? "38px" : "44px",
        }}
      >
        <span>{label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
        {isActive && !isOpen && (
          <span
            aria-hidden="true"
            className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#0FA0FF] shadow-[0_0_6px_#0FA0FF]"
          />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className="absolute right-0 top-[calc(100%+12px)] w-[300px] p-2"
            style={{
              background: "linear-gradient(150deg, #062A59 0%, #073A70 100%)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: "20px",
              boxShadow: "0 22px 54px rgba(5, 42, 92, 0.45)",
            }}
          >
            {eyebrow && (
              <div className="px-3 pt-2 pb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">
                  {eyebrow}
                </span>
              </div>
            )}
            {items.map((svc) => {
              const isAnchor = svc.href.includes("#");
              if (isAnchor) {
                return (
                  <a
                    key={svc.href}
                    role="menuitem"
                    href={svc.href}
                    onClick={() => {
                      setIsOpen(false);
                      onNavigate();
                    }}
                    className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl font-sans text-[14px] font-medium text-white/80 hover:text-white hover:bg-white/[0.08] transition-all duration-150"
                  >
                    <span>{svc.name}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-[#16A7FF] transition-colors duration-150" />
                  </a>
                );
              }
              return (
                <Link
                  key={svc.href}
                  role="menuitem"
                  to={svc.href}
                  onClick={() => {
                    setIsOpen(false);
                    onNavigate();
                  }}
                  className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl font-sans text-[14px] font-medium text-white/80 hover:text-white hover:bg-white/[0.08] transition-all duration-150"
                >
                  <span>{svc.name}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-[#16A7FF] transition-colors duration-150" />
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Work");
  const headerRef = useRef<HTMLElement>(null);
  // Set stable initial nav height and update on resize only (avoiding forced reflow on every scroll)
  useEffect(() => {
    let ticking = false;
    const updateNavHeight = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const el = headerRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const bottomEdge = Math.ceil(
            rect.bottom > 0 ? rect.bottom + 36 : el.offsetHeight + 44,
          );
          const effectiveHeight = Math.max(bottomEdge, 130);
          document.documentElement.style.setProperty(
            "--nav-height",
            `${effectiveHeight}px`,
          );
        }
        ticking = false;
      });
    };

    updateNavHeight();
    window.addEventListener("resize", updateNavHeight, { passive: true });

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateNavHeight)
        : null;
    if (headerRef.current && ro) ro.observe(headerRef.current);

    return () => {
      window.removeEventListener("resize", updateNavHeight);
      ro?.disconnect();
    };
  }, []);

  // Handle scroll for compact state + active section observer with rAF throttling
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 40);

          const scrollPos = window.scrollY + 200;
          for (const item of SECTION_IDS) {
            const el = document.getElementById(item.id);
            if (el) {
              const top = el.offsetTop;
              const height = el.offsetHeight;
              if (scrollPos >= top && scrollPos < top + height) {
                setActiveItem(item.name);
                break;
              }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const handleNavClick = (itemName: string) => {
    setActiveItem(itemName);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header
        ref={headerRef}
        role="banner"
        className="fixed top-0 left-0 right-0 z-50 pointer-events-none transition-all duration-300 px-3 sm:px-6 lg:px-8"
        style={{
          paddingTop: isScrolled ? "10px" : "16px",
        }}
      >
        <div className="max-w-[1520px] mx-auto pointer-events-auto">
          <nav
            aria-label="Main Navigation"
            className="w-full relative flex items-center justify-between transition-all duration-300"
            style={{
              background:
                "linear-gradient(110deg, #05264F 0%, #073466 48%, #062A59 100%)",
              border: "1px solid rgba(255, 255, 255, 0.10)",
              borderRadius: "9999px",
              boxShadow: isScrolled
                ? "0 18px 42px rgba(5, 42, 92, 0.28), 0 4px 12px rgba(5, 42, 92, 0.14)"
                : "0 16px 40px rgba(5, 42, 92, 0.20), 0 4px 12px rgba(5, 42, 92, 0.10)",
              height: isScrolled ? "72px" : "88px",
              padding: isScrolled ? "8px 14px 8px 18px" : "10px 16px 10px 22px",
            }}
          >
            {/* LEFT: Brand Identity */}
            <a
              href="/#hero"
              className="flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A7FF] rounded-full shrink-0"
              aria-label="Marjohn Robillo - Home"
            >
              <div className="relative shrink-0">
                <div
                  className="rounded-full overflow-hidden transition-all duration-300"
                  style={{
                    width: isScrolled ? "40px" : "48px",
                    height: isScrolled ? "40px" : "48px",
                    border: "2px solid rgba(28, 156, 255, 0.75)",
                    boxShadow: "0 0 0 3px rgba(11, 124, 242, 0.12)",
                  }}
                >
                  <img
                    src={AVATAR_URL}
                    alt="Marjohn Robillo"
                    width={44}
                    height={44}
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span
                  aria-hidden="true"
                  className="absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-[#05264F]"
                  style={{
                    width: isScrolled ? "10px" : "11px",
                    height: isScrolled ? "10px" : "11px",
                    backgroundColor: "#32F064",
                    boxShadow: "0 0 6px rgba(50, 240, 100, 0.6)",
                  }}
                />
              </div>

              <div className="flex flex-col text-left">
                <span
                  className="font-heading font-extrabold text-white tracking-[-0.025em] leading-tight transition-all duration-300"
                  style={{ fontSize: isScrolled ? "17px" : "20px" }}
                >
                  Marjohn Robillo
                </span>
                <span
                  className="font-sans font-semibold uppercase tracking-[0.19em] text-white/60 transition-all duration-300 hidden sm:block"
                  style={{ fontSize: isScrolled ? "9px" : "10px" }}
                >
                  Revenue Systems Architect
                </span>
              </div>
            </a>

            {/* CENTER: Navigation Capsule (Desktop) */}
            <div className="hidden lg:flex items-center justify-center">
              <div
                className="flex items-center gap-1 transition-all duration-300"
                style={{
                  background: "rgba(255, 255, 255, 0.055)",
                  border: "1px solid rgba(255, 255, 255, 0.10)",
                  borderRadius: "9999px",
                  padding: "5px 6px",
                  height: isScrolled ? "48px" : "56px",
                }}
              >
                {NAV_ITEMS.map((item) => {
                  const isActive = activeItem === item.name;
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={() => handleNavClick(item.name)}
                      className={`relative flex flex-col items-center justify-center font-sans font-semibold rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A7FF] ${
                        isActive
                          ? "text-white bg-white/10 shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
                          : "text-white/75 hover:text-white hover:bg-white/[0.07]"
                      }`}
                      style={{
                        fontSize: isScrolled ? "14px" : "15px",
                        padding: isScrolled ? "0 20px" : "0 24px",
                        height: isScrolled ? "38px" : "44px",
                      }}
                    >
                      <span>{item.name}</span>
                      {isActive && (
                        <span
                          aria-hidden="true"
                          className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#0FA0FF] shadow-[0_0_6px_#0FA0FF]"
                        />
                      )}
                    </a>
                  );
                })}

                {/* Services dropdown — sits right after Story */}
                <NavDropdown
                  label="Services"
                  items={SERVICE_ITEMS}
                  isOpen={servicesOpen}
                  setIsOpen={setServicesOpen}
                  isScrolled={isScrolled}
                  onNavigate={() => setMobileMenuOpen(false)}
                />

                {/* Resources dropdown — right-most, contains Blog + FAQ */}
                <NavDropdown
                  label="Resources"
                  items={RESOURCE_ITEMS}
                  isOpen={resourcesOpen}
                  setIsOpen={setResourcesOpen}
                  isScrolled={isScrolled}
                  activeId={activeItem === "FAQ" ? "faq" : undefined}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              </div>
            </div>

            {/* RIGHT: Primary Book a Call CTA */}
            <div className="hidden lg:flex items-center">
              <a
                href="/book"
                className="group inline-flex items-center justify-center gap-2 font-sans font-bold text-white rounded-full transition-all duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A7FF]"
                style={{
                  background:
                    "linear-gradient(135deg, #0A8FF5 0%, #119DF8 100%)",
                  boxShadow:
                    "0 8px 22px rgba(10, 143, 245, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
                  height: isScrolled ? "48px" : "54px",
                  padding: isScrolled ? "0 26px" : "0 32px",
                  fontSize: isScrolled ? "14px" : "15px",
                }}
              >
                <span>Book a Call</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </div>

            {/* MOBILE: Hamburger Button */}
            <div className="flex lg:hidden items-center">
              <button
                type="button"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center justify-center rounded-full text-white transition-all duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16A7FF]"
                style={{
                  width: "46px",
                  height: "46px",
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.10)",
                }}
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* MOBILE OPEN MENU */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed top-20 sm:top-24 right-4 sm:right-6 z-[60] w-[calc(100vw-32px)] max-w-[380px] p-4 text-white"
            style={{
              background: "linear-gradient(150deg, #062A59 0%, #073A70 100%)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: "26px",
              boxShadow: "0 22px 54px rgba(5, 42, 92, 0.45)",
            }}
          >
            {/* Header row in mobile dropdown */}
            <div className="flex items-center justify-between pb-3 mb-2 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div
                  className="rounded-full overflow-hidden shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    border: "2px solid rgba(28, 156, 255, 0.75)",
                  }}
                >
                  <img
                    src={AVATAR_URL}
                    alt="Marjohn Robillo"
                    width={36}
                    height={36}
                    loading="eager"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col text-left">
                  <span className="font-heading font-extrabold text-[15px] text-white leading-tight">
                    Marjohn Robillo
                  </span>
                  <span className="font-sans font-semibold text-[8px] uppercase tracking-[0.16em] text-white/60">
                    Revenue Systems Architect
                  </span>
                </div>
              </div>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileMenuOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Navigation links */}
            <div className="flex flex-col gap-1 py-1">
              {NAV_ITEMS.map((item) => {
                const isActive = activeItem === item.name;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => handleNavClick(item.name)}
                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl font-sans text-[15px] font-semibold transition-all duration-150 ${
                      isActive
                        ? "text-white bg-white/10"
                        : "text-white/80 hover:text-white hover:bg-white/[0.06]"
                    }`}
                  >
                    <span>{item.name}</span>
                    {isActive && (
                      <span
                        aria-hidden="true"
                        className="w-2 h-2 rounded-full bg-[#12A0FF] shadow-[0_0_6px_#12A0FF]"
                      />
                    )}
                  </a>
                );
              })}

              {/* Services collapsible group (mobile) */}
              <div className="rounded-xl overflow-hidden">
                <button
                  type="button"
                  aria-expanded={mobileServicesOpen}
                  onClick={() => setMobileServicesOpen((v) => !v)}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl font-sans text-[15px] font-semibold transition-all duration-150 ${
                    mobileServicesOpen
                      ? "text-white bg-white/10"
                      : "text-white/80 hover:text-white hover:bg-white/[0.06]"
                  }`}
                >
                  <span>Services</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      mobileServicesOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {mobileServicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-0.5 pt-1 pb-1 pl-2">
                        {SERVICE_ITEMS.map((svc) => (
                          <Link
                            key={svc.href}
                            to={svc.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg font-sans text-[13.5px] font-medium text-white/70 hover:text-white hover:bg-white/[0.06] transition-all duration-150"
                          >
                            <span>{svc.name}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-white/30" />
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Resources collapsible group (mobile) */}
              <div className="rounded-xl overflow-hidden">
                <button
                  type="button"
                  aria-expanded={mobileResourcesOpen}
                  onClick={() => setMobileResourcesOpen((v) => !v)}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl font-sans text-[15px] font-semibold transition-all duration-150 ${
                    mobileResourcesOpen
                      ? "text-white bg-white/10"
                      : "text-white/80 hover:text-white hover:bg-white/[0.06]"
                  }`}
                >
                  <span>Resources</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      mobileResourcesOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {mobileResourcesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-0.5 pt-1 pb-1 pl-2">
                        {RESOURCE_ITEMS.map((r) => {
                          const isActive = r.id && activeItem === "FAQ";
                          return (
                            <a
                              key={r.href}
                              href={r.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg font-sans text-[13.5px] font-medium transition-all duration-150 ${
                                isActive
                                  ? "text-white bg-white/[0.08]"
                                  : "text-white/70 hover:text-white hover:bg-white/[0.06]"
                              }`}
                            >
                              <span>{r.name}</span>
                              <ChevronRight className="w-3.5 h-3.5 text-white/30" />
                            </a>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Bottom Book a Call CTA */}
            <a
              href="/book"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-3 w-full flex items-center justify-center gap-2 font-sans font-bold text-white rounded-full transition-all duration-200"
              style={{
                height: "52px",
                background: "linear-gradient(135deg, #0A8FF5 0%, #119DF8 100%)",
                boxShadow: "0 8px 22px rgba(10, 143, 245, 0.35)",
                fontSize: "15px",
              }}
            >
              <span>Book a Call</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
